import React from 'react'
import { Button } from '../ui'
import { GoalHeader } from './GoalHeader.jsx'
import { GoalStatsStrip } from './GoalStatsStrip.jsx'
import { SubgoalsSection } from './SubgoalsSection.jsx'
import { CommentsSection } from './CommentsSection.jsx'
import { useGoal } from './hooks/useGoal.js'
import { useGoals } from './hooks/useGoals.js'
import { useSubgoal } from './hooks/useSubgoal.js'

export function GoalDetailPage({ goalId, me, ownerUserId = null, onBack, onGoalChanged }) {
  const effectiveOwner = ownerUserId || me?.userId
  const { goal, loading, reload } = useGoal({ goalId, ownerUserId: effectiveOwner })
  const { updateGoal: rawUpdateGoal, activateGoal: rawActivateGoal, achieveGoal: rawAchieveGoal, dropGoal: rawDropGoal, reopenGoal: rawReopenGoal, setStatusLabel: rawSetStatusLabel } = useGoals({ ownerUserId: effectiveOwner })

  // Wrap mutations to refresh both the detail page and the sidebar
  const notify = (fn) => async (...args) => { const r = await fn(...args); reload(); onGoalChanged?.(); return r }
  const updateGoal = notify(rawUpdateGoal)
  const activateGoal = notify(rawActivateGoal)
  const achieveGoal = notify(rawAchieveGoal)
  const dropGoal = notify(rawDropGoal)
  const reopenGoal = notify(rawReopenGoal)
  const setStatusLabel = notify(rawSetStatusLabel)
  const {
    createSubgoal, deleteSubgoal, setSubgoalStatus, updateKpi,
    toggleDeliverable, addDeliverable, removeDeliverable,
    toggleMilestone, addMilestone, removeMilestone,
    updateCriteria, updateDeadline, updateSubgoalNotes, updateSubgoal,
    addComment, deleteComment,
  } = useSubgoal({ ownerUserId: effectiveOwner })

  async function handleAddComment(text) {
    await addComment(goalId, text)
    reload()
  }

  async function handleDeleteComment(entryId) {
    await deleteComment(goalId, entryId)
    reload()
  }

  // Wrap sub-goal mutations to reload after each
  const withReload = (fn) => async (...args) => { await fn(...args); reload() }

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <Button variant="ghost" size="sm" onClick={onBack}>← Back</Button>
      </div>

      {loading && !goal && <p style={styles.muted}>Loading...</p>}

      {goal && (
        <div style={styles.content}>
          <GoalHeader
            goal={goal}
            onUpdateGoal={updateGoal}
            onActivate={activateGoal}
            onAchieve={achieveGoal}
            onDrop={dropGoal}
            onReopen={reopenGoal}
            onSetStatusLabel={setStatusLabel}
          />

          <GoalStatsStrip
            rollup={goal.rollup}
            timeHorizon={goal.timeHorizon}
          />

          <SubgoalsSection
            goalId={goalId}
            subgoals={goal.subgoals || []}
            onCreateSubgoal={withReload(createSubgoal)}
            onDeleteSubgoal={withReload(deleteSubgoal)}
            onSetSubgoalStatus={withReload(setSubgoalStatus)}
            onUpdateKpi={updateKpi}
            onToggleDeliverable={withReload(toggleDeliverable)}
            onAddDeliverable={withReload(addDeliverable)}
            onRemoveDeliverable={withReload(removeDeliverable)}
            onToggleMilestone={withReload(toggleMilestone)}
            onAddMilestone={withReload(addMilestone)}
            onRemoveMilestone={withReload(removeMilestone)}
            onUpdateCriteria={withReload(updateCriteria)}
            onUpdateDeadline={withReload(updateDeadline)}
            onUpdateNotes={updateSubgoalNotes}
            onUpdateTitle={withReload((goalId, subgoalId, title) => updateSubgoal(goalId, subgoalId, { title }))}
            parentTimeHorizon={goal.timeHorizon}
          />

          <CommentsSection
            comments={goal.comments || []}
            currentUserId={me?.userId}
            onAdd={handleAddComment}
            onDelete={handleDeleteComment}
          />
        </div>
      )}
    </div>
  )
}

const styles = {
  page: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  muted: { fontSize: '13px', color: 'var(--text-secondary)' },
}
